import { datadogRum } from '@datadog/browser-rum-slim';
import { datadogLogs } from '@datadog/browser-logs';

function startRumRecording() {
  return window.location.search.includes('hjDebugSession=1');
}

export const getThirdPartyEnvironmentId = () => {
  const h = document.location.host;

  if (h === 'insights.hotjar.com') {
    return 'live';
  }
  const result = h.match(/^insights-(.+)\.review\.eks\.hotjar\.com$/);
  if (result) {
    return result[2];
  }
  if (h === 'insights-integration.live.eks.hotjar.com') {
    return 'integration';
  }

  return 'dev';
};

const environmentId = getThirdPartyEnvironmentId();
const isRecording = startRumRecording();

export function initDataDogRum({ serviceName, version }) {
  if (environmentId !== 'dev') {
    const clientToken = 'pub9dd2d53aedc0e75487200c45ada37190';
    const applicationId = '4736f14b-19f2-439c-b435-602757312b46';

    datadogRum.init({
      applicationId,
      clientToken,
      site: 'datadoghq.eu',
      service: serviceName || 'engage-legacy-frontend',
      version,
      env: environmentId,
      sampleRate: 100,
      allowedTracingOrigins: [/https:\/\/.*\.hotjar\.com/, /https:\/\/.*\.hotjar\.io/],
      excludedActivityUrls: [/^(?!.*(.hotjar.com|s3.amazonaws.com))/],
      useSecureSessionCookie: true,

      /*
        trackUserInteractions
        Elements marked with data-dd-action-name will be tracked as user actions.
        https://docs.datadoghq.com/real_user_monitoring/browser/tracking_user_actions/
      */
      trackUserInteractions: true,

      /*
        trackViewsManually
        Allows teams to override the default view names for the RUM views.
        https://docs.datadoghq.com/real_user_monitoring/browser/modifying_data_and_context/?tab=npm#override-default-rum-view-names
      */
      trackViewsManually: true
    });

    if (isRecording) {
      datadogRum.startSessionReplayRecording();
    }
  }

  window.datadogRum = datadogRum;
}

export function startView(name) {
  const filterUrl = (str) =>
    str
      .replace(/\/([0-9]+)(\/|$)/g, '/:id/')
      .replace(/\/[a-zA-Z0-9]{22}(\/|$)/g, '/:id/')
      .replace(/\/y4[a-zA-Z0-9]{14}(\/|$)/g, '/:admin/')
      .replace(/\/[a-zA-Z0-9]{6}[-_][a-zA-Z0-9]{32}(\/|$)/, '/:token/')
      .replace(/\/[a-zA-Z0-9]{32}(\/|$)/g, '/:token/')
      .replace(/\/[^/@]+@[^/@]+(\/|$)/g, '/:email/')
      .replace(/\/reset\/[a-zA-Z0-9]+(\/|$)/, '/reset/:token/')
      .replace(/\/unsubscribe_invites\/.*(\/|$)/, '/unsubscribe_invites/:email/:token/');

  const viewName = name || filterUrl(window.location.pathname);

  window.datadogRum?.startView({ name: viewName });
}

/**
 * @summary Sends the handled error to Datadog RUM
 * @param {(string|Error)} error The caught error
 * @param  {object} [metadata] Metadata for the error
 * @return {undefined}
 * @example addRumError(error, {withMetaData: true});
 */
export function addRumError(error, metadata) {
  window.datadogRum?.addError(error, metadata);
}

/**
 * @summary Sends custom RUM action to Datadog, useful for associating error with user actions
 * @param  {string} actionName Name of the action
 * @param  {object} [metadata] Metadata for the action
 * @return {undefined}
 * @example addRumAction('interview-user-clicked-this-thing', {withMetaData: true});
 */
export function addRumAction(actionName, metadata) {
  window.datadogRum?.addAction(actionName, metadata);
}

/**
 * @summary Extends the global context for RUM events
 * @param  {string} propertyName Name of the context property
 * @param  {string|object} value Value of the context property
 * @return {undefined}
 * @example setGlobalContextProperty('session-id', 123456');
 */
export function setGlobalContextProperty(propertyName, context) {
  window.datadogRum?.setGlobalContextProperty(propertyName, context);
}

export function initDataDogLogs({ serviceName, version }) {
  datadogLogs.init({
    clientToken: 'pub980ac3345f76d59f07e84bd74b4733c4',
    site: 'datadoghq.eu',
    service: serviceName || 'engage-legacy-frontend',
    version,
    env: environmentId,
    forwardErrorsToLogs: false, // Errors are tracked with RUM
    sessionSampleRate: 100,
    forwardReports: ['deprecation', 'csp_violation'],
    telemetrySampleRate: 0,
    useSecureSessionCookie: true
  });
}

/**
 * @typedef {Function} Log
 * @param {string} msg - The message to log
 * @param {Object} ctx - Context for the log
 */

/**
 * @typedef {Object} DatadogLogger
 * @property {Log} error
 * @property {Log} warn
 * @property {Log} info
 * @property {Log} log
 * @property {Log} debug
 */

/**
 * @summary Create a logger for Datadog
 * @param  {string} name Name of the logger
 * @return {DatadogLogger}
 * @example const logger = createLogger('room-events');
 */
export const createLogger = (name) => {
  const context = window.datadogRum?.getInternalContext();
  const logger = window.datadogLogs?.createLogger(name, { team: 'Engage', ...context }) ?? console;

  const wrap = (log) => (msg, ctx) => {
    const appContext = window.datadogRum?.getGlobalContext();
    log(msg, {
      ...ctx,
      ...appContext
    });
  };

  return {
    error: wrap(logger.error),
    warn: wrap(logger.warn),
    info: wrap(logger.info),
    log: wrap(logger.log),
    debug: wrap(logger.debug)
  };
};
